import React, { useState } from 'react';
import { submitQuoteRequest } from '../api/config';

const Quote = () => {
  const [formData, setFormData] = useState({
    artistName: '',
    email: '',
    phone: '',
    location: '',
    currentOperation: '',
    servicesNeeded: '',
    offersPerMonth: '',
    inventoryCount: '',
    needsStorage: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await submitQuoteRequest(formData);
      setIsSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-mint-50 py-16">
        <div className="max-w-md mx-auto text-center px-4">
          <div className="w-20 h-20 bg-mint-400 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
          <p className="text-lg text-gray-600">
            Thank you for reaching out to us! You should hear back from MerchMint in 2-4 business days.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-mint-300 to-mint-400 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-purple-600 mb-4">
            Get a Quote!
          </h1>
          <p className="text-lg text-purple-600/80 max-w-2xl mx-auto">
            Ready to bring your merchandise vision to life? Tell us about your project and we'll get back to you with a custom quote.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Artist/Company Name */}
            <div>
              <label htmlFor="artistName" className="block text-sm font-medium text-gray-700 mb-2">
                Artist/Company Name *
              </label>
              <input
                type="text"
                id="artistName"
                name="artistName"
                required
                value={formData.artistName}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-400 focus:border-mint-400 transition-colors"
                placeholder="Your name or company"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-400 focus:border-mint-400 transition-colors"
                placeholder="your@email.com"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-400 focus:border-mint-400 transition-colors"
                placeholder="(555) 123-4567"
              />
            </div>

            {/* Location */}
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                Business Location (City, State, Zipcode) *
              </label>
              <input
                type="text"
                id="location"
                name="location"
                required
                value={formData.location}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-400 focus:border-mint-400 transition-colors"
                placeholder="Los Angeles, CA 90001"
              />
            </div>

            {/* Current Operation */}
            <div>
              <label htmlFor="currentOperation" className="block text-sm font-medium text-gray-700 mb-2">
                Describe Your Current Operation *
              </label>
              <textarea
                id="currentOperation"
                name="currentOperation"
                required
                rows={4}
                value={formData.currentOperation}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-400 focus:border-mint-400 transition-colors resize-none"
                placeholder="Tell us about your current merchandise setup, if any..."
              />
            </div>

            {/* Services Needed */}
            <div>
              <label htmlFor="servicesNeeded" className="block text-sm font-medium text-gray-700 mb-2">
                Describe What Services You Need *
              </label>
              <textarea
                id="servicesNeeded"
                name="servicesNeeded"
                required
                rows={4}
                value={formData.servicesNeeded}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-400 focus:border-mint-400 transition-colors resize-none"
                placeholder="What are you looking for help with? (e.g., t-shirt printing, vinyl production, fulfillment...)"
              />
            </div>

            {/* Numbers Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Offers Per Month */}
              <div>
                <label htmlFor="offersPerMonth" className="block text-sm font-medium text-gray-700 mb-2">
                  # of Expected Orders Per Month
                </label>
                <input
                  type="number"
                  id="offersPerMonth"
                  name="offersPerMonth"
                  min="0"
                  value={formData.offersPerMonth}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-400 focus:border-mint-400 transition-colors"
                  placeholder="100"
                />
              </div>

              {/* Inventory Count */}
              <div>
                <label htmlFor="inventoryCount" className="block text-sm font-medium text-gray-700 mb-2">
                  # Items in Current Inventory
                </label>
                <input
                  type="number"
                  id="inventoryCount"
                  name="inventoryCount"
                  min="0"
                  value={formData.inventoryCount}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-400 focus:border-mint-400 transition-colors"
                  placeholder="500"
                />
              </div>
            </div>

            {/* Storage Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="needsStorage"
                name="needsStorage"
                checked={formData.needsStorage}
                onChange={handleChange}
                className="h-5 w-5 text-mint-500 focus:ring-mint-400 border-gray-300 rounded cursor-pointer"
              />
              <label htmlFor="needsStorage" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
                Do you need storage?
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 px-6 text-white font-semibold rounded-full bg-purple-600 hover:bg-purple-700 focus:ring-4 focus:ring-purple-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </span>
              ) : (
                'Submit Quote Request'
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Quote;
